import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Clock, DollarSign, Calendar } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { updateTimeclockEntry, TimeclockError, type UpdateEntryResult } from '../../lib/timeclock';
import { usePayrollHoursText } from '../../hooks/usePayrollHoursText';

interface PayrollEntry {
  id: string;
  resource_id: string;
  resource_first_name: string;
  resource_last_name: string;
  resource_email: string;
  compensation_type: string;
  flat_rate_amount: number | null;
  hourly_rate: number | null;
  booking_id: string | null;
  booking_number: number | null;
  booking_title: string | null;
  start_date: string | null;
  end_date: string | null;
  entry_type: 'flat_rate' | 'timeclock' | 'commission' | 'bonus' | 'tip' | 'adjustment';
  hours: number;
  earnings: number;
  rate_amount?: number | null;
  item_name: string | null;
  item_description: string | null;
  created_at: string;
  clock_in?: string;
  clock_out?: string;
  break_start?: string;
  break_end?: string;
  total_minutes?: number;
  notes?: string;
  status?: string;
  process_status?: string;
  completed_at?: string;
}

interface PayrollEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  entry: PayrollEntry | null;
  onSave: (entryId: string, updates: any) => Promise<void>;
  /** Called after a timeclock shift has been saved through the engine. */
  onTimeclockUpdated?: (result: UpdateEntryResult) => void | Promise<void>;
  onDelete?: (entryId: string) => Promise<void>;
  // For creating new entries
  resourceId?: string;
  businessId?: string;
  resourceName?: string;
  createKind?: 'timeclock' | 'bonus';
  onCreate?: (entryData: any) => Promise<void>;
}

interface EditFormData {
  // Timeclock fields
  clockIn: string;
  clockOut: string;
  breakStart: string;
  breakEnd: string;
  hourlyRate: number | string;
  
  // Flat rate fields
  hours: number | string;
  flatRateAmount: number | string;
  
  // Commission fields
  earnings: number | string;
  
  // Bonus fields
  bonusAmount: number | string;
  
  // Common fields
  notes: string;
  itemName: string;
  itemDescription: string;
}

export default function PayrollEditModal({ isOpen, onClose, entry, onSave, onTimeclockUpdated, onDelete, resourceId, businessId, resourceName, createKind = 'timeclock', onCreate }: PayrollEditModalProps) {
  const isCreateMode = !entry;
  const creatingBonus = isCreateMode && createKind === 'bonus';
  // A paid shift needs an explicit confirmation before the engine records an adjustment.
  const [paidConfirm, setPaidConfirm] = useState(false);
  const [paidReason, setPaidReason] = useState('');
  // Rate the form opened with; only an explicit change is sent as an override.
  const [initialRate, setInitialRate] = useState<string>('');
  const [formData, setFormData] = useState<EditFormData>({
    clockIn: '',
    clockOut: '',
    breakStart: '',
    breakEnd: '',
    hourlyRate: 0,
    hours: 0,
    flatRateAmount: 0,
    earnings: 0,
    bonusAmount: 0,
    notes: '',
    itemName: '',
    itemDescription: ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [resourceHourlyRate, setResourceHourlyRate] = useState<number | null>(null);

  // Helper function to convert UTC timestamp to local timezone for form inputs
  const formatDateTimeForInput = (dateStr: string | null | undefined): string => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '';
    
    // Convert to local timezone for the form input
    const offset = date.getTimezoneOffset() * 60000;
    const localDate = new Date(date.getTime() - offset);
    return localDate.toISOString().slice(0, 16);
  };

  // Helper function to convert form datetime to UTC
  const convertToUTC = (dateTimeString: string): string | null => {
    if (!dateTimeString) return null;
    const localDate = new Date(dateTimeString);
    return localDate.toISOString();
  };

  // Fetch resource hourly rate when in create mode
  useEffect(() => {
    if (isCreateMode && resourceId && isOpen) {
      const fetchResourceRate = async () => {
        if (!supabase) return;
        
        try {
          const { data, error } = await supabase
            .from('resources')
            .select('hourly_rate')
            .eq('id', resourceId)
            .single();

          if (error) {
            console.error('Error fetching resource hourly rate:', error);
            setResourceHourlyRate(0);
          } else {
            setResourceHourlyRate(data?.hourly_rate || 0);
          }
        } catch (err) {
          console.error('Error fetching resource hourly rate:', err);
          setResourceHourlyRate(0);
        }
      };

      fetchResourceRate();
    } else {
      setResourceHourlyRate(null);
    }
  }, [isCreateMode, resourceId, isOpen]);

  // Initialize form data when entry changes
  useEffect(() => {
    if (entry) {
      setFormData({
        clockIn: formatDateTimeForInput(entry.clock_in),
        clockOut: formatDateTimeForInput(entry.clock_out),
        breakStart: formatDateTimeForInput(entry.break_start),
        breakEnd: formatDateTimeForInput(entry.break_end),
        // Pay rate is the snapshot stored on the row, not the resource's current rate
        hourlyRate: entry.rate_amount ?? entry.hourly_rate ?? 0,
        hours: entry.hours || 0,
        flatRateAmount: entry.rate_amount ?? entry.flat_rate_amount ?? 0,
        earnings: entry.earnings || 0,
        bonusAmount: entry.earnings || 0, // For bonus entries, earnings represents the bonus amount
        notes: entry.notes || '',
        itemName: entry.item_name || '',
        itemDescription: entry.item_description || ''
      });
      setError(null);
      setPaidConfirm(false);
      setPaidReason('');
      setInitialRate(String(entry.rate_amount ?? entry.hourly_rate ?? 0));
    } else if (isCreateMode) {
      // Initialize with current date/time for create mode
      const now = new Date();
      const offset = now.getTimezoneOffset() * 60000;
      const localDate = new Date(now.getTime() - offset);
      const defaultDateTime = localDate.toISOString().slice(0, 16);
      
      setFormData({
        clockIn: defaultDateTime,
        clockOut: '',
        breakStart: '',
        breakEnd: '',
        hourlyRate: 0,
        hours: 0,
        flatRateAmount: 0,
        earnings: 0,
        bonusAmount: 0,
        notes: '',
        itemName: createKind === 'bonus' ? 'Bonus' : '',
        itemDescription: ''
      });
      setError(null);
    }
  }, [entry, isCreateMode, createKind]);

  // Handle form input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    // For number inputs, keep as string for controlled input, but validate
    // We'll convert to number when saving
    const processedValue = type === 'number' ? value : value;
    setFormData(prev => ({
      ...prev,
      [name]: processedValue
    }));
  };

  // Calculate totals for preview
  const calculateTotals = () => {
    if (creatingBonus) {
      const bonusAmount = typeof formData.bonusAmount === 'number' ? formData.bonusAmount : parseFloat(String(formData.bonusAmount)) || 0;
      return { totalMinutes: 0, totalHours: 0, totalEarnings: bonusAmount };
    }
    const entryType = entry?.entry_type || 'timeclock';
    
    if (entryType === 'timeclock') {
      if (formData.clockIn && formData.clockOut) {
        const start = new Date(formData.clockIn);
        const end = new Date(formData.clockOut);
        let totalMs = end.getTime() - start.getTime();
        
        // Subtract break time if applicable
        if (formData.breakStart && formData.breakEnd) {
          const breakStart = new Date(formData.breakStart);
          const breakEnd = new Date(formData.breakEnd);
          totalMs -= (breakEnd.getTime() - breakStart.getTime());
        }
        
        const totalMinutes = Math.max(0, Math.floor(totalMs / (1000 * 60)));
        const totalHours = totalMinutes / 60;
        // Use resource's hourly rate in create mode, otherwise use form data
        const hourlyRate = isCreateMode 
          ? (resourceHourlyRate ?? 0)
          : (typeof formData.hourlyRate === 'number' ? formData.hourlyRate : parseFloat(String(formData.hourlyRate)) || 0);
        const totalEarnings = totalHours * hourlyRate;
        
        return { totalMinutes, totalHours, totalEarnings };
      }
    } else if (entry && entry.entry_type === 'flat_rate') {
      const hours = typeof formData.hours === 'number' ? formData.hours : parseFloat(String(formData.hours)) || 0;
      const flatRateAmount = typeof formData.flatRateAmount === 'number' ? formData.flatRateAmount : parseFloat(String(formData.flatRateAmount)) || 0;
      const totalEarnings = hours * flatRateAmount;
      return { totalMinutes: hours * 60, totalHours: hours, totalEarnings };
    } else if (entry && entry.entry_type === 'commission') {
      const earnings = typeof formData.earnings === 'number' ? formData.earnings : parseFloat(String(formData.earnings)) || 0;
      return { totalMinutes: 0, totalHours: 0, totalEarnings: earnings };
    } else if (entry && entry.entry_type === 'bonus') {
      const bonusAmount = typeof formData.bonusAmount === 'number' ? formData.bonusAmount : parseFloat(String(formData.bonusAmount)) || 0;
      return { totalMinutes: 0, totalHours: 0, totalEarnings: bonusAmount };
    } else if (entry && entry.entry_type === 'tip') {
      return { totalMinutes: 0, totalHours: 0, totalEarnings: entry.earnings || 0 };
    }
    
    return { totalMinutes: 0, totalHours: 0, totalEarnings: 0 };
  };

  const { totalMinutes, totalHours, totalEarnings } = calculateTotals();
  const hoursText = usePayrollHoursText([totalMinutes]);

  // Handle save
  const handleSave = async () => {
    setIsSubmitting(true);
    setError(null);
    
    try {
      // Handle create mode
      if (isCreateMode && onCreate && resourceId && businessId) {
        if (creatingBonus) {
          const bonusAmount = typeof formData.bonusAmount === 'number' ? formData.bonusAmount : parseFloat(String(formData.bonusAmount));
          if (!Number.isFinite(bonusAmount) || bonusAmount <= 0) {
            throw new Error('Enter a bonus amount greater than zero');
          }
          await onCreate({
            kind: 'bonus',
            amount: bonusAmount,
            item_name: formData.itemName.trim() || 'Bonus',
            item_description: formData.itemDescription.trim() || null,
            notes: formData.notes.trim() || null,
          });
          onClose();
          return;
        }
        if (!formData.clockIn) {
          throw new Error('Clock in time is required');
        }

        const entryData = {
          clock_in: convertToUTC(formData.clockIn)!,
          clock_out: convertToUTC(formData.clockOut),
          break_start: convertToUTC(formData.breakStart),
          break_end: convertToUTC(formData.breakEnd),
          notes: formData.notes || null
        };

        await onCreate(entryData);
        onClose();
        return;
      }

      // Handle edit mode
      if (!entry) return;

      if (entry.entry_type === 'timeclock') {
        // Shifts are edited through the engine: it recomputes minutes, keeps the
        // rate snapshot and re-runs overtime. Editing a paid shift records an adjustment.
        if (!formData.clockIn) throw new Error('Clock in time is required');
        const rateInput = String(formData.hourlyRate ?? '').trim();
        const rateChanged = rateInput !== '' && parseFloat(rateInput) !== parseFloat(initialRate || '0');
        const rateAmount = rateChanged ? parseFloat(rateInput) : null;
        try {
          const result = await updateTimeclockEntry(entry.id, {
            clockIn: convertToUTC(formData.clockIn)!,
            clockOut: convertToUTC(formData.clockOut),
            breakStart: convertToUTC(formData.breakStart),
            breakEnd: convertToUTC(formData.breakEnd),
            notes: formData.notes || null,
            rateAmount: rateAmount !== null && Number.isFinite(rateAmount) ? rateAmount : null,
            allowPaid: paidConfirm,
            reason: paidConfirm ? (paidReason.trim() || null) : null,
          });
          setPaidConfirm(false);
          setPaidReason('');
          await onTimeclockUpdated?.(result);
          onClose();
        } catch (err) {
          if (err instanceof TimeclockError && err.code === 'entry_paid') {
            setPaidConfirm(true);
            setError(null);
            return;
          }
          throw err;
        }
        return;
      }

      const updates: any = {
        notes: formData.notes || null,
        updated_at: new Date().toISOString()
      };

      if (entry.entry_type === 'flat_rate') {
        updates.hours = typeof formData.hours === 'number' ? formData.hours : parseFloat(String(formData.hours)) || 0;
        updates.rate_amount = typeof formData.flatRateAmount === 'number' ? formData.flatRateAmount : parseFloat(String(formData.flatRateAmount)) || 0;
        updates.total_earnings = totalEarnings;
        updates.item_name = formData.itemName || null;
        updates.item_description = formData.itemDescription || null;
      } else if (entry.entry_type === 'commission') {
        const commissionAmount = typeof formData.earnings === 'number' ? formData.earnings : parseFloat(String(formData.earnings)) || 0;
        updates.total_earnings = commissionAmount;
        // Note: 'earnings' column doesn't exist in payroll_page table
        updates.item_name = formData.itemName || null;
        updates.item_description = formData.itemDescription || null;
      } else if (entry.entry_type === 'bonus') {
        // Convert bonus amount to number, ensuring it's valid
        const bonusAmountStr = String(formData.bonusAmount || '0').trim();
        const bonusAmount = bonusAmountStr === '' ? 0 : parseFloat(bonusAmountStr);
        
        if (isNaN(bonusAmount) || bonusAmount < 0) {
          throw new Error('Bonus amount must be a valid positive number');
        }
        
        // Only update columns that exist in the database
        updates.bonus_earnings = bonusAmount;
        updates.total_earnings = bonusAmount;
        updates.total_amount = bonusAmount;
        // Note: 'earnings' column doesn't exist in payroll_page table
        updates.item_name = formData.itemName || null;
        updates.item_description = formData.itemDescription || null;
      }

      await onSave(entry.id, updates);
      // Close modal after successful save
      onClose();
    } catch (err) {
      console.error('Error saving payroll entry:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to save changes';
      setError(errorMessage);
      // Log full error details for debugging
      if (err && typeof err === 'object') {
        console.error('Full error object:', err);
        if ('details' in err) {
          console.error('Error details:', err.details);
        }
        if ('hint' in err) {
          console.error('Error hint:', err.hint);
        }
        if ('code' in err) {
          console.error('Error code:', err.code);
        }
      }
      // Don't close modal on error - let user see the error and try again
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle delete
  const handleDelete = async () => {
    if (!entry || !onDelete) return;
    
    setIsDeleting(true);
    setError(null);
    
    try {
      await onDelete(entry.id);
      setShowDeleteConfirmation(false);
      onClose();
    } catch (err) {
      console.error('Error deleting payroll entry:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete entry');
    } finally {
      setIsDeleting(false);
    }
  };

  if (!isOpen) return null;
  
  // For create mode, we need resourceId and businessId
  if (isCreateMode && (!resourceId || !businessId)) return null;
  
  // For edit mode, we need entry
  if (!isCreateMode && !entry) return null;

  const entryType = entry?.entry_type || 'timeclock';
  const entryTypeLabel = entryType === 'flat_rate' ? 'Flat Rate' : entryType === 'timeclock' ? 'Timeclock' : entryType.charAt(0).toUpperCase() + entryType.slice(1);

  const modalContent = (
    <>
      <div
        className="fixed inset-0 z-[9999] bg-black/50 transition-opacity duration-200"
        style={{ backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)' }}
        onClick={onClose}
      />

      <div
        className="fixed inset-y-0 right-0 z-[9999] w-full sm:w-[520px] bg-white shadow-2xl flex flex-col transform transition-transform duration-200 translate-x-0"
        style={{
          top: 'env(safe-area-inset-top, 0px)',
          bottom: 'env(safe-area-inset-bottom, 0px)',
          fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200/60 bg-white flex-shrink-0">
          <div className="min-w-0">
            <h2 className="text-[17px] font-semibold text-gray-900 truncate">
              {creatingBonus ? 'Add bonus' : isCreateMode ? 'Create Timeclock Entry' : 'Edit Payroll Entry'}
            </h2>
            {creatingBonus && resourceName && (
              <p className="text-[13px] text-gray-500 mt-0.5 truncate">{resourceName}</p>
            )}
            {!isCreateMode && entry && (
              <div className="flex items-center gap-2 mt-0.5">
                <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded text-[11px] font-semibold">
                  {entryTypeLabel}
                </span>
                <span className="text-[13px] text-gray-500">
                  {entry.resource_first_name} {entry.resource_last_name}
                </span>
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-2 -mr-2 hover:bg-gray-100 active:bg-gray-200 rounded-xl transition-colors touch-manipulation"
            style={{ minWidth: 44, minHeight: 44 }}
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Form Content */}
        <div className="flex-1 overflow-y-auto bg-gray-50/50">
          <div className="px-4 py-4 space-y-5">
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-[13px] text-red-700">
                {error}
              </div>
            )}

            {paidConfirm && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl space-y-2">
                <p className="text-[13px] font-semibold text-amber-900">This shift has already been paid</p>
                <p className="text-[13px] text-amber-800">
                  Saving will keep the paid amount on record and add the difference as a pay adjustment on the next payroll run.
                </p>
                <input
                  type="text"
                  value={paidReason}
                  onChange={(e) => setPaidReason(e.target.value)}
                  placeholder="Reason for the correction (optional)"
                  className="w-full px-3 py-2 text-[14px] border border-amber-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-amber-300"
                />
                <p className="text-[12px] text-amber-700">Press Save again to record the adjustment.</p>
              </div>
            )}

            {/* Timeclock Entry Fields */}
            {!creatingBonus && (isCreateMode || entryType === 'timeclock') && (
              <div className="bg-white rounded-xl border border-gray-200/60 overflow-hidden shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
                <div className="px-4 py-2.5 border-b border-gray-100 bg-gray-50/50">
                  <p className="text-[13px] font-semibold text-gray-500 uppercase tracking-wide">Time</p>
                </div>
                <div className="p-4 space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-[13px] font-medium text-gray-600 mb-1.5">Clock In</label>
                      <input
                        type="datetime-local"
                        name="clockIn"
                        value={formData.clockIn}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2.5 text-[15px] border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 bg-white"
                      />
                    </div>
                    <div>
                      <label className="block text-[13px] font-medium text-gray-600 mb-1.5">Clock Out</label>
                      <input
                        type="datetime-local"
                        name="clockOut"
                        value={formData.clockOut}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2.5 text-[15px] border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 bg-white"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-[13px] font-medium text-gray-600 mb-1.5">Break Start</label>
                      <input
                        type="datetime-local"
                        name="breakStart"
                        value={formData.breakStart}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2.5 text-[15px] border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 bg-white"
                      />
                    </div>
                    <div>
                      <label className="block text-[13px] font-medium text-gray-600 mb-1.5">Break End</label>
                      <input
                        type="datetime-local"
                        name="breakEnd"
                        value={formData.breakEnd}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2.5 text-[15px] border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 bg-white"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Flat Rate Entry Fields */}
            {entry && entryType === 'flat_rate' && (
              <div className="bg-white rounded-xl border border-gray-200/60 overflow-hidden shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
                <div className="px-4 py-2.5 border-b border-gray-100 bg-gray-50/50">
                  <p className="text-[13px] font-semibold text-gray-500 uppercase tracking-wide">Details</p>
                </div>
                <div className="p-4 space-y-4">
                  <div>
                    <label className="block text-[13px] font-medium text-gray-600 mb-1.5">Item Name</label>
                    <input type="text" name="itemName" value={formData.itemName} onChange={handleInputChange}
                      className="w-full px-3 py-2.5 text-[15px] border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 bg-white" />
                  </div>
                  <div>
                    <label className="block text-[13px] font-medium text-gray-600 mb-1.5">Description</label>
                    <input type="text" name="itemDescription" value={formData.itemDescription} onChange={handleInputChange}
                      className="w-full px-3 py-2.5 text-[15px] border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 bg-white" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-[13px] font-medium text-gray-600 mb-1.5">Hours</label>
                      <input type="number" name="hours" value={formData.hours} onChange={handleInputChange} step="0.1" min="0"
                        className="w-full px-3 py-2.5 text-[15px] border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 bg-white" />
                    </div>
                    <div>
                      <label className="block text-[13px] font-medium text-gray-600 mb-1.5">Rate / Hour</label>
                      <input type="number" name="flatRateAmount" value={formData.flatRateAmount} onChange={handleInputChange} step="0.01" min="0"
                        className="w-full px-3 py-2.5 text-[15px] border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 bg-white" />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Commission Entry Fields */}
            {entry && entryType === 'commission' && (
              <div className="bg-white rounded-xl border border-gray-200/60 overflow-hidden shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
                <div className="px-4 py-2.5 border-b border-gray-100 bg-gray-50/50">
                  <p className="text-[13px] font-semibold text-gray-500 uppercase tracking-wide">Commission</p>
                </div>
                <div className="p-4 space-y-4">
                  <div>
                    <label className="block text-[13px] font-medium text-gray-600 mb-1.5">Item Name</label>
                    <input type="text" name="itemName" value={formData.itemName} onChange={handleInputChange}
                      className="w-full px-3 py-2.5 text-[15px] border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 bg-white" />
                  </div>
                  <div>
                    <label className="block text-[13px] font-medium text-gray-600 mb-1.5">Description</label>
                    <input type="text" name="itemDescription" value={formData.itemDescription} onChange={handleInputChange}
                      className="w-full px-3 py-2.5 text-[15px] border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 bg-white" />
                  </div>
                  <div>
                    <label className="block text-[13px] font-medium text-gray-600 mb-1.5">Amount</label>
                    <input type="number" name="earnings" value={formData.earnings} onChange={handleInputChange} step="0.01" min="0"
                      className="w-full px-3 py-2.5 text-[15px] border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 bg-white" />
                  </div>
                </div>
              </div>
            )}

            {/* Bonus Entry Fields */}
            {(creatingBonus || (entry && entryType === 'bonus')) && (
              <div className="bg-white rounded-xl border border-gray-200/60 overflow-hidden shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
                <div className="px-4 py-2.5 border-b border-gray-100 bg-gray-50/50">
                  <p className="text-[13px] font-semibold text-gray-500 uppercase tracking-wide">Bonus</p>
                </div>
                <div className="p-4 space-y-4">
                  <div>
                    <label className="block text-[13px] font-medium text-gray-600 mb-1.5">Item Name</label>
                    <input type="text" name="itemName" value={formData.itemName} onChange={handleInputChange}
                      className="w-full px-3 py-2.5 text-[15px] border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 bg-white" />
                  </div>
                  <div>
                    <label className="block text-[13px] font-medium text-gray-600 mb-1.5">Description</label>
                    <input type="text" name="itemDescription" value={formData.itemDescription} onChange={handleInputChange}
                      className="w-full px-3 py-2.5 text-[15px] border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 bg-white" />
                  </div>
                  <div>
                    <label className="block text-[13px] font-medium text-gray-600 mb-1.5">Amount</label>
                    <input type="number" name="bonusAmount" value={formData.bonusAmount} onChange={handleInputChange} step="0.01" min="0"
                      className="w-full px-3 py-2.5 text-[15px] border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 bg-white" />
                  </div>
                </div>
              </div>
            )}

            {/* Tip Entry Fields (read-only) */}
            {entry && entryType === 'tip' && (
              <div className="bg-white rounded-xl border border-gray-200/60 overflow-hidden shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
                <div className="px-4 py-2.5 border-b border-gray-100 bg-gray-50/50">
                  <p className="text-[13px] font-semibold text-gray-500 uppercase tracking-wide">Tip</p>
                </div>
                <div className="p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-[13px] text-gray-500">Amount</span>
                    <span className="text-[17px] font-bold text-gray-900">${(entry.earnings || 0).toFixed(2)}</span>
                  </div>
                  {entry.item_description && (
                    <div className="pt-2 border-t border-gray-100">
                      <span className="text-[13px] text-gray-500">Description</span>
                      <p className="text-[15px] text-gray-900 mt-0.5">{entry.item_description}</p>
                    </div>
                  )}
                  {entry.booking_number && (
                    <div className="pt-2 border-t border-gray-100">
                      <span className="text-[13px] text-gray-500">Booking</span>
                      <p className="text-[15px] text-gray-900 mt-0.5">#{entry.booking_number}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Notes */}
            <div className="bg-white rounded-xl border border-gray-200/60 overflow-hidden shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
              <div className="p-4">
                <label className="block text-[13px] font-medium text-gray-600 mb-1.5">Notes</label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full px-3 py-2.5 text-[15px] border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 bg-white resize-none"
                  placeholder="Optional notes..."
                />
              </div>
            </div>

            {/* Calculated Totals */}
            {(isCreateMode || (entry && (entryType === 'timeclock' || entryType === 'flat_rate' || entryType === 'bonus' || entryType === 'tip'))) && (
              <div className="bg-white rounded-xl border border-gray-200/60 overflow-hidden shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
                <div className="px-4 py-2.5 border-b border-gray-100 bg-gray-50/50">
                  <p className="text-[13px] font-semibold text-gray-500 uppercase tracking-wide">Summary</p>
                </div>
                <div className="p-4 space-y-2">
                  {!creatingBonus && (isCreateMode || entryType === 'timeclock') && (
                    <div className="flex items-center justify-between">
                      <span className="text-[13px] text-gray-500">Total Time</span>
                      <span className="text-[15px] font-medium text-gray-900">{hoursText(totalMinutes)}</span>
                    </div>
                  )}
                  {isCreateMode && !creatingBonus && resourceHourlyRate !== null && (
                    <div className="flex items-center justify-between">
                      <span className="text-[13px] text-gray-500">Hourly Rate</span>
                      <span className="text-[15px] font-medium text-gray-900">${resourceHourlyRate.toFixed(2)}/hr</span>
                    </div>
                  )}
                  {entry && entryType === 'flat_rate' && (
                    <div className="flex items-center justify-between">
                      <span className="text-[13px] text-gray-500">Hours</span>
                      <span className="text-[15px] font-medium text-gray-900">{(typeof totalHours === 'number' ? totalHours : parseFloat(String(totalHours)) || 0).toFixed(1)}h</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                    <span className="text-[13px] font-semibold text-gray-700">{entryType === 'timeclock' && !creatingBonus ? 'Estimated Earnings' : 'Total Earnings'}</span>
                    <span className="text-[17px] font-bold text-green-600">${(typeof totalEarnings === 'number' ? totalEarnings : parseFloat(String(totalEarnings)) || 0).toFixed(2)}</span>
                  </div>
                  {entryType === 'timeclock' && !creatingBonus && (
                    <p className="text-[12px] text-gray-400">Final pay, including any overtime, is calculated when you save.</p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 border-t border-gray-200/60 bg-white" style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom, 0.75rem))' }}>
          {!showDeleteConfirmation ? (
            <div className="px-4 py-3 flex gap-3">
              {!isCreateMode && entry && (entryType === 'timeclock' || entryType === 'flat_rate' || entryType === 'bonus' || entryType === 'tip') && onDelete && (
                <button
                  type="button"
                  className="px-4 py-2.5 text-[15px] font-medium text-red-600 bg-red-50 rounded-xl hover:bg-red-100 active:bg-red-200 transition-colors touch-manipulation"
                  onClick={() => setShowDeleteConfirmation(true)}
                  disabled={isSubmitting}
                >
                  Delete
                </button>
              )}
              <div className="flex-1" />
              <button
                type="button"
                className="px-5 py-2.5 text-[15px] font-medium text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 active:bg-gray-300 transition-colors touch-manipulation"
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="px-5 py-2.5 text-[15px] font-semibold text-white bg-blue-600 rounded-xl hover:bg-blue-700 active:bg-blue-800 transition-colors touch-manipulation disabled:opacity-50"
                onClick={handleSave}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <span className="flex items-center gap-2">
                    <span className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Saving…
                  </span>
                ) : (
                  creatingBonus ? 'Add bonus' : isCreateMode ? 'Create' : paidConfirm ? 'Save & record adjustment' : 'Save'
                )}
              </button>
            </div>
          ) : (
            <div className="px-4 py-3 space-y-3">
              <div className="bg-red-50 border border-red-200 rounded-xl p-3">
                <p className="text-[13px] text-red-700">
                  Are you sure you want to delete this {entryTypeLabel.toLowerCase()} entry? This cannot be undone.
                </p>
              </div>
              <div className="flex gap-3">
                <div className="flex-1" />
                <button
                  type="button"
                  className="px-5 py-2.5 text-[15px] font-medium text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors touch-manipulation"
                  onClick={() => setShowDeleteConfirmation(false)}
                  disabled={isDeleting}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="px-5 py-2.5 text-[15px] font-semibold text-white bg-red-600 rounded-xl hover:bg-red-700 transition-colors touch-manipulation disabled:opacity-50"
                  onClick={handleDelete}
                  disabled={isDeleting}
                >
                  {isDeleting ? (
                    <span className="flex items-center gap-2">
                      <span className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Deleting…
                    </span>
                  ) : 'Delete'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );

  return createPortal(modalContent, document.body);
}